import React, { useState } from 'react';
import { Grid, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { FormProvider, RHFFileField } from '../../../common/hook-form';
import { apiConstants, postMultipart } from '../../../common/constants';
import DownloadSampleFile from '../../../common/DownloadSampleFile';

// Shared bulk address-update form — used by both Channel Orders and Bulk Orders.
// Matches rows against any ChannelOrder for the merchant, whichever channel it's from
// (see ChannelController#bulkUpdateAddress: order_number, then reference, then channelOrderId).
export default function BulkAddressUpload({ merchantId, handleCloseBulkAddressUpload, setUploadResults, setUploadSummary, setIsResultModalOpen }) {
  const { enqueueSnackbar } = useSnackbar()
  const [isUploadingFile, setIsUploadingFile] = useState(false)

  const methods = useForm({
    defaultValues: {
      addressFile: null,
    },
  })

  const { handleSubmit, reset } = methods

  const onSubmit = async (data) => {
    setIsUploadingFile(true)
    try {
      const formData = new FormData()
      formData.append('file', data.addressFile[0])
      formData.append('merchantOid', merchantId)
      const res = await postMultipart(apiConstants.BULK_UPDATE_ADDRESS, formData)

      if (res.status === 200 && res.data?.results?.length > 0) {
        const { results, successCount, failureCount, totalRows } = res?.data

        const formattedResults = results.map((result, index) => ({
          id: result.rowNumber || index,
          rowNumber: result.rowNumber,
          orderNumber: result.orderNumber || '-',
          channelOrderId: result.channelOrderId || '-',
          status: result.status,
          message: result.message || result.errorMessage || '-',
        }))

        setUploadResults(formattedResults)
        setUploadSummary({ successCount, failureCount, totalRows })

        if (handleCloseBulkAddressUpload) {
          handleCloseBulkAddressUpload()
        }
        setIsResultModalOpen(true)

        if (successCount > 0) {
          enqueueSnackbar(`Successfully updated ${successCount} out of ${totalRows} addresses`, { variant: 'success' })
        } else {
          enqueueSnackbar(`Failed to update addresses. Please check the results.`, { variant: 'error' })
        }

        reset()
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Address update failed', { variant: 'error' })
    } finally {
      setIsUploadingFile(false)
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RHFFileField
                name="addressFile"
                label="Click here to upload address file"
                fileType={['.xlsx', '.xls', '.csv']}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                variant='contained'
                color='primary'
                onClick={handleSubmit(onSubmit)}
                loading={isUploadingFile}
                type="submit"
              >
                Upload
              </LoadingButton>
              <Button
                variant='contained'
                color='error'
                sx={{ ml: 1 }}
                onClick={() => {
                  reset()
                }}
              >
                Reset
              </Button>
              <DownloadSampleFile
                fileName="CHANNEL_ORDER_BULK_ADDRESS_UPDATE_SAMPLE"
                label="Download Sample"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Grid>
    </Grid>
  )
}
